<script setup lang="ts">
import { defineAsyncComponent } from 'vue'
import { LazyMaintenancePage } from '#components'

const Error404 = defineAsyncComponent(() => import('~/components/error/404.vue'))
const Error500 = defineAsyncComponent(() => import('~/components/error/500.vue'))
const ErrorDev = defineAsyncComponent(() => import('~/components/error/Dev.vue'))

const props = defineProps<{
    error: {
        statusCode?: number
        statusMessage?: string
        message?: string
        description?: string
        stack?: string
        data?: unknown
    }
}>()

const stacktrace = (props.error.stack ?? '')
    .split('\n')
    .splice(1)
    .map((line) => {
        const text = line
            .replace('webpack:/', '')
            .replace('.vue', '.js')
            .trim()
        return {
            text,
            internal: (line.includes('node_modules') && !line.includes('.cache')) ||
                line.includes('internal') ||
                line.includes('new Promise')
        }
    }).map(i => `<span class="stack${i.internal ? ' internal' : ''}">${i.text}</span>`).join('\n')

const isDebugMode = useCookie<number>('a4g-debug').value === 1
const errorData = props.error.data
    ? `\n\n<pre style="margin-top:1.5rem;padding-top:1.5rem;border-top:1px solid rgba(255,255,255,0.15);color:#36E4DA;word-break:break-all;white-space:pre-wrap">${JSON.stringify(props.error.data, null, 2)}</pre>`
    : ''

const statusCode = props.error.statusCode ?? 500
const isMaintenance = isMaintenanceError(props.error)
// 410 Gone reads like "not found" to a visitor,
// so it shares the 404 template — only the status code and message stay accurate.
const is404 = statusCode === 404 || statusCode === 410
const statusMessage = props.error.statusMessage ?? (is404 ? 'Page Not Found' : 'Internal Server Error')
const description = props.error.message ?? props.error.toString()
const stack = isDebugMode ? props.error.description ?? `<pre>${stacktrace}</pre>${errorData}` : undefined

const ErrorTemplate = isMaintenance
    ? LazyMaintenancePage
    : is404
        ? Error404
        : isDebugMode
            ? ErrorDev
            : Error500
</script>

<template>
    <component
        :is="ErrorTemplate"
        :status-code="statusCode"
        :status-message="statusMessage"
        :description="description"
        :stack="stack"
    />
</template>
